import { useQuery, useMutation, QueryResult } from '@apollo/client';
// API
import {
  GET_MY_CB_SUBSCRIPTION,
  GET_CB_PLANS,
  GET_AGENCY,
} from 'api/brandSubscriptions/queries';
import {
  CANCEL_CB_SUBSCRIPTION_DOWNGRADE,
  VOID_CANCEL_CB_SUBSCRIPTION,
  SET_CB_ENTITLEMENT,
  UPDATE_MY_CB_SUBSCRIPTION,
  CANCEL_CB_SUBSCRIPTION,
} from 'api/brandSubscriptions/mutations';
// Types
import {
  GetMyCbSubscription,
  GetMyCbSubscriptionVariables,
} from 'api/brandSubscriptions/types/GetMyCbSubscription';
import { GetCbPlans } from 'api/brandSubscriptions/types/GetCbPlans';
import {
  CancelCbSubscriptionDowngrade,
  CancelCbSubscriptionDowngradeVariables,
} from 'api/brandSubscriptions/types/CancelCbSubscriptionDowngrade';
import {
  VoidCancelCbSubscription,
  VoidCancelCbSubscriptionVariables,
} from 'api/brandSubscriptions/types/VoidCancelCbSubscription';
import {
  UpdateMyCbSubscription,
  UpdateMyCbSubscriptionVariables,
} from 'api/brandSubscriptions/types/UpdateMyCbSubscription';
import {
  CancelCbSubscription,
  CancelCbSubscriptionVariables,
} from 'api/brandSubscriptions/types/CancelCbSubscription';
import {
  GetAgency,
  GetAgencyVariables,
} from 'api/brandSubscriptions/types/GetAgency';

type UseBrandDetailsOptions = {
  ownerId: string | null | undefined;
};

type UseBrandDetailsReturn = {
  subscriptionQuery: QueryResult<
    GetMyCbSubscription,
    GetMyCbSubscriptionVariables
  >;
  plansQuery: QueryResult<GetCbPlans>;
  agencyQuery: QueryResult<GetAgency, GetAgencyVariables>;
  cancelDowngrade: (
    variables: CancelCbSubscriptionDowngradeVariables
  ) => Promise<any>;
  voidCancellation: (
    variables: VoidCancelCbSubscriptionVariables
  ) => Promise<any>;
  updateSubscription: (
    variables: UpdateMyCbSubscriptionVariables
  ) => Promise<any>;
  cancelSubscription: (
    variables: CancelCbSubscriptionVariables
  ) => Promise<any>;
  setEntitlement: (variables: any) => Promise<any>;
};

export const useBrandDetails = ({
  ownerId,
}: UseBrandDetailsOptions): UseBrandDetailsReturn => {
  const subscriptionQuery = useQuery<
    GetMyCbSubscription,
    GetMyCbSubscriptionVariables
  >(GET_MY_CB_SUBSCRIPTION, {
    variables: { input: { ownerId: ownerId || '' } },
    skip: !ownerId,
  });

  const plansQuery = useQuery<GetCbPlans>(GET_CB_PLANS);

  const agencyQuery = useQuery<GetAgency, GetAgencyVariables>(GET_AGENCY, {
    variables: { input: { agencyId: ownerId || '' } },
    skip: !ownerId,
  });

  const [cancelDowngradeMutation] = useMutation<
    CancelCbSubscriptionDowngrade,
    CancelCbSubscriptionDowngradeVariables
  >(CANCEL_CB_SUBSCRIPTION_DOWNGRADE);

  const [voidCancellationMutation] = useMutation<
    VoidCancelCbSubscription,
    VoidCancelCbSubscriptionVariables
  >(VOID_CANCEL_CB_SUBSCRIPTION);

  const [updateSubscriptionMutation] = useMutation<
    UpdateMyCbSubscription,
    UpdateMyCbSubscriptionVariables
  >(UPDATE_MY_CB_SUBSCRIPTION);

  const [cancelSubscriptionMutation] = useMutation<
    CancelCbSubscription,
    CancelCbSubscriptionVariables
  >(CANCEL_CB_SUBSCRIPTION);

  const [setEntitlementMutation] = useMutation(SET_CB_ENTITLEMENT);

  const cancelDowngrade = async (
    variables: CancelCbSubscriptionDowngradeVariables
  ) => {
    return cancelDowngradeMutation({ variables });
  };

  const voidCancellation = async (
    variables: VoidCancelCbSubscriptionVariables
  ) => {
    return voidCancellationMutation({ variables });
  };

  const updateSubscription = async (
    variables: UpdateMyCbSubscriptionVariables
  ) => {
    return updateSubscriptionMutation({ variables });
  };

  const cancelSubscription = async (
    variables: CancelCbSubscriptionVariables
  ) => {
    return cancelSubscriptionMutation({ variables });
  };

  const setEntitlement = async (variables: any) => {
    return setEntitlementMutation({ variables });
  };

  return {
    subscriptionQuery,
    plansQuery,
    agencyQuery,
    cancelDowngrade,
    voidCancellation,
    updateSubscription,
    cancelSubscription,
    setEntitlement,
  };
};
